import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonIcon,
} from '@ionic/react';
import { 
  Box, 
  Typography, 
  Avatar, 
  Paper, 
  CircularProgress, 
  Button, 
  Chip,
  Modal,
  Fade,
  IconButton,
  Divider
} from '@mui/material';
import { 
  ChatBubbleOutline, 
  Business as BusinessIcon, 
  Person as PersonIcon,
  Campaign as CampaignIcon,
  Work as WorkIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { heart, heartOutline, expand } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Post } from '../types/post';
import { BaseUser } from '../types/user';
import { doc, getDoc, collection, query, where, updateDoc, increment, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Company, Contractor, Ambassador } from '../types/user';
import { useAuthContext } from '../contexts/AuthContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { customGradients } from '../theme/theme';

export const PostView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const [post, setPost] = useState<Post | null>(null);
  const [author, setAuthor] = useState<BaseUser | null>(null);
  const [authorSpecialization, setAuthorSpecialization] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { userData: currentUser, user: authUser } = useAuthContext();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imagesLoaded, setImagesLoaded] = useState<{[key: number]: boolean}>({});
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    let isSubscribed = true;

    const fetchPost = async () => {
      if (!id) return;
      
      setError(null);
      setLoading(true);
      
      try {
        // Try to get the post from Firestore
        const postRef = doc(db, 'posts', id);
        const postDoc = await getDoc(postRef);
        
        if (!isSubscribed) return;
        
        if (postDoc.exists()) {
          const postData = { id: postDoc.id, ...postDoc.data() } as Post;
          setPost(postData);
          setLikeCount(postData.likes || 0);
          setIsLiked(postData.likedBy ? postData.likedBy.includes(authUser?.uid || '') : false);
          
          // Fetch author data
          try {
            const authorDoc = await getDoc(doc(db, 'users', postData.authorId));
            if (authorDoc.exists() && isSubscribed) {
              const authorData = authorDoc.data() as BaseUser;
              setAuthor(authorData);
              
              // Get specialization based on user type
              if (authorData.type === 'company') {
                setAuthorSpecialization((authorData as Company).specialization || null);
              } else if (authorData.type === 'contractor') {
                setAuthorSpecialization((authorData as Contractor).specialization || null);
              } else if (authorData.type === 'ambassador') {
                // For ambassador, get the activity type as specialization
                const ambassador = authorData as Ambassador;
                if (ambassador.activityType) {
                  // Handle both string and CustomField object
                  const activityType = typeof ambassador.activityType === 'string'
                    ? ambassador.activityType
                    : ambassador.activityType.isOther
                      ? (ambassador.activityType.customValue || '')
                      : ambassador.activityType.value;
                  
                  // Capitalize first letter
                  setAuthorSpecialization(
                    activityType ? activityType.charAt(0).toUpperCase() + activityType.slice(1) : null
                  );
                }
              }
            }
          } catch (authorError) {
            console.error('Error fetching author data:', authorError);
            // Continue showing the post even if author data can't be fetched
          }
        } else {
          setError(t('post.not_found'));
        }
      } catch (error) {
        if (!isSubscribed) return;
        console.error('Error fetching post:', error);
        setError(t('post.error_loading'));
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    };

    fetchPost();

    return () => {
      isSubscribed = false;
    };
  }, [id, t, authUser?.uid]);

  const handleLike = async () => {
    if (!authUser?.uid || !post) return;
    
    try {
      // Update like status in UI immediately for better UX
      setIsLiked(!isLiked);
      setLikeCount(isLiked ? likeCount - 1 : likeCount + 1);
      
      // Update in database
      const postRef = doc(db, 'posts', post.id);
      const newLikedBy = isLiked 
        ? (post.likedBy || []).filter(id => id !== authUser.uid)
        : [...(post.likedBy || []), authUser.uid];
      
      await updateDoc(postRef, {
        likes: isLiked ? increment(-1) : increment(1),
        likedBy: newLikedBy
      });
    } catch (error) {
      // Revert UI changes if the update fails
      setIsLiked(isLiked);
      setLikeCount(likeCount);
      console.error('Error updating like:', error);
    }
  };

  const createConversation = async () => {
    if (!authUser?.uid || !post?.authorId) return;
    
    try {
      // Check if a conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', authUser.uid)
      );
      const querySnapshot = await getDocs(q);
      
      let existingConversation = null;
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.participants.includes(post.authorId)) {
          existingConversation = { id: doc.id, ...data };
        }
      });
      
      if (existingConversation) {
        // Redirect to existing conversation
        history.push(`/messages?conversation=${existingConversation.id}`);
      } else {
        // Create a new conversation
        const newConversation = await addDoc(conversationsRef, {
          participants: [authUser.uid, post.authorId],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          unreadCount: {
            [authUser.uid]: 0,
            [post.authorId]: 0
          }
        });
        
        // Redirect to the new conversation
        history.push(`/messages?conversation=${newConversation.id}`);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const formatDate = (timestamp: any) => {
    let date;
    if (timestamp?.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (error) {
    return (
      <IonPage>
        {/* Modern Custom Header */}
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1100,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.85)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease',
            py: 1.5
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              maxWidth: 800,
              mx: 'auto',
              px: 2
            }}
          >
            <IconButton 
              onClick={() => history.push('/home')}
              sx={{ 
                color: '#0068B9',
                mr: 1,
                '&:hover': {
                  background: 'rgba(0, 104, 185, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                background: customGradients.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px'
              }}
            >
              {t('post.view_title')}
            </Typography>
          </Box>
        </Box>
        
        {/* Spacer to prevent content from being hidden under the fixed header */}
        <Box sx={{ height: '70px' }} />
        
        <IonContent>
          <Box sx={{ p: 4, textAlign: 'center', mt: 4 }}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 4, 
                borderRadius: '24px',
                background: 'rgba(255, 255, 255, 0.9)',
                maxWidth: 600,
                mx: 'auto',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)'
              }}
              className="glass-effect animate-slideUp"
            >
              <Typography 
                color="error" 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </Typography>
            </Paper>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  if (loading) {
    return (
      <IonPage>
        {/* Modern Custom Header */}
        <Box 
          sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1100,
            backdropFilter: 'blur(10px)',
            background: 'rgba(255, 255, 255, 0.85)',
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
            transition: 'all 0.3s ease',
            py: 1.5
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              maxWidth: 800,
              mx: 'auto',
              px: 2
            }}
          >
            <IconButton 
              onClick={() => history.push('/home')}
              sx={{ 
                color: '#0068B9',
                mr: 1,
                '&:hover': {
                  background: 'rgba(0, 104, 185, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                background: customGradients.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px'
              }}
            >
              {t('post.view_title')}
            </Typography>
          </Box>
        </Box>
        
        {/* Spacer to prevent content from being hidden under the fixed header */}
        <Box sx={{ height: '70px' }} />
        
        <IonContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: 'calc(100vh - 70px)',
            gap: 2
          }}>
            <CircularProgress 
              size={60} 
              sx={{ 
                color: '#0068B9',
                boxShadow: '0 0 30px rgba(0, 104, 185, 0.2)'
              }} 
            />
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                mt: 2,
                animation: 'pulse 2s infinite'
              }}
            >
              {t('post.loading')}
            </Typography>
          </Box>
        </IonContent>
      </IonPage>
    );
  }

  const isAuthor = currentUser && authUser && authUser.uid === post.authorId;

  return (
    <IonPage>
      {/* Modern Custom Header */}
      <Box 
        sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          zIndex: 1100,
          backdropFilter: 'blur(10px)',
          background: 'rgba(255, 255, 255, 0.85)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.05)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
          py: 1.5
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            maxWidth: 800,
            mx: 'auto',
            px: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => history.push('/home')}
              sx={{ 
                color: '#0068B9',
                mr: 1,
                '&:hover': {
                  background: 'rgba(0, 104, 185, 0.1)',
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                background: customGradients.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '0.5px'
              }}
            >
              {t('post.view_title')}
            </Typography>
          </Box>
          
          {/* Message button - only show if not the author */}
          {!isAuthor && post.authorId && authUser && (
            <Button
              variant="contained"
              startIcon={<ChatBubbleOutline />}
              onClick={createConversation}
              size="small"
              sx={{
                borderRadius: '12px',
                background: customGradients.primary,
                boxShadow: '0 4px 12px rgba(0, 104, 185, 0.2)',
                transition: 'all 0.3s ease',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 2,
                py: 0.8,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0, 104, 185, 0.3)',
                }
              }}
            >
              {t('post.message_author')}
            </Button>
          )}
        </Box>
      </Box>
      
      {/* Spacer to prevent content from being hidden under the fixed header */}
      <Box sx={{ height: '70px' }} />

      <IonContent>
        <Box sx={{ pb: 12, maxWidth: 800, mx: 'auto' }}>
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: '24px',
              overflow: 'hidden',
              mb: 3,
              position: 'relative',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
              }
            }}
            className="glass-effect animate-slideUp"
          >
            {/* Author Info */}
            <Box 
              sx={{ 
                p: 3, 
                display: 'flex', 
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                background: 'rgba(255, 255, 255, 0.5)'
              }}
            >
              <Avatar
                src={post.authorPhotoURL}
                alt={post.authorName}
                sx={{ 
                  width: 56, 
                  height: 56, 
                  mr: 2,
                  border: '2px solid',
                  borderColor: 'primary.light',
                  boxShadow: '0 4px 12px rgba(0, 104, 185, 0.15)'
                }}
              />
              <Box>
                <Typography 
                  variant="h6" 
                  component="div"
                  sx={{ 
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                  onClick={() => history.push(`/profile/${post.authorId}`)}
                >
                  {post.authorName}
                </Typography>
                {author?.type && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={t(`auth.${author.type}`)}
                        avatar={
                          <Avatar
                            src={
                              author.type === 'company' ? '/assets/image/types/Entreprise.png' :
                              author.type === 'contractor' ? '/assets/image/types/Prestataire.png' :
                              author.type === 'ambassador' ? '/assets/image/types/Affiliateur.png' :
                              '/assets/image/logo.png'
                            }
                            sx={{
                              width: 18,
                              height: 18
                            }}
                          />
                        }
                        sx={{
                          background: 'rgba(0, 104, 185, 0.1)',
                          color: 'primary.main',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: '22px'
                        }}
                      />
                    </Box>
                    
                    {authorSpecialization && (author.type === 'company' || author.type === 'contractor') && (
                      <Chip
                        size="small"
                        label={authorSpecialization}
                        sx={{
                          background: 'rgba(145, 213, 112, 0.2)',
                          color: '#2e7d32',
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: '22px',
                          maxWidth: '200px',
                          mt: 0.5,
                          '& .MuiChip-label': {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            px: 1
                          }
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ p: 3 }}>
              {/* Post Title */}
              <Typography
                variant="h5"
                component="h1"
                sx={{ 
                  mb: 2.5, 
                  fontWeight: 'bold',
                  color: 'primary.main'
                }}
              >
                {post.title}
              </Typography>

              {/* Images */}
              {post.images && post.images.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  {/* Image Gallery */}
                  {post.images.length === 1 ? (
                    // Single image display
                    <Box 
                      sx={{ 
                        position: 'relative', 
                        borderRadius: '16px', 
                        overflow: 'hidden',
                        height: '400px',
                        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {!imagesLoaded[0] && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          height: '100%',
                          width: '100%',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          background: 'rgba(255, 255, 255, 0.8)'
                        }}>
                          <CircularProgress />
                        </Box>
                      )}
                      <img
                        src={post.images[0]}
                        alt={`${t('post.image')} 1`}
                        onLoad={() => setImagesLoaded(prev => ({ ...prev, [0]: true }))}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <IconButton
                        onClick={() => setSelectedImage(post.images[0])}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          backdropFilter: 'blur(4px)',
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                      >
                        <IonIcon icon={expand} style={{ color: 'white' }} />
                      </IconButton>
                    </Box>
                  ) : post.images.length === 2 ? (
                    // Two images side by side
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: 1,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
                    }}>
                      {post.images.map((image, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            position: 'relative', 
                            height: '300px',
                            overflow: 'hidden'
                          }}
                        >
                          {!imagesLoaded[index] && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center',
                              height: '100%',
                              width: '100%',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              background: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              <CircularProgress />
                            </Box>
                          )}
                          <img
                            src={image}
                            alt={`${t('post.image')} ${index + 1}`}
                            onLoad={() => setImagesLoaded(prev => ({ ...prev, [index]: true }))}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            onClick={() => setSelectedImage(image)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                          >
                            <IonIcon icon={expand} style={{ color: 'white' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : post.images.length === 3 ? (
                    // Three images - one large, two small
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: '2fr 1fr',
                      gridTemplateRows: 'repeat(2, 150px)',
                      gap: 1,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
                    }}>
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          gridRow: 'span 2',
                          overflow: 'hidden'
                        }}
                      >
                        {!imagesLoaded[0] && (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            background: 'rgba(255, 255, 255, 0.8)'
                          }}>
                            <CircularProgress />
                          </Box>
                        )}
                        <img
                          src={post.images[0]}
                          alt={`${t('post.image')} 1`}
                          onLoad={() => setImagesLoaded(prev => ({ ...prev, [0]: true }))}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          onClick={() => setSelectedImage(post.images[0])}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            backdropFilter: 'blur(4px)',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.5)',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                          }}
                        >
                          <IonIcon icon={expand} style={{ color: 'white' }} />
                        </IconButton>
                      </Box>
                      {post.images.slice(1, 3).map((image, index) => (
                        <Box 
                          key={index + 1}
                          sx={{ 
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {!imagesLoaded[index + 1] && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center',
                              height: '100%',
                              width: '100%',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              background: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              <CircularProgress />
                            </Box>
                          )}
                          <img
                            src={image}
                            alt={`${t('post.image')} ${index + 2}`}
                            onLoad={() => setImagesLoaded(prev => ({ ...prev, [index + 1]: true }))}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            onClick={() => setSelectedImage(image)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                          >
                            <IonIcon icon={expand} style={{ color: 'white' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : post.images.length === 4 ? (
                    // Four images in a grid
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gridTemplateRows: 'repeat(2, 150px)',
                      gap: 1,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
                    }}>
                      {post.images.map((image, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {!imagesLoaded[index] && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center',
                              height: '100%',
                              width: '100%',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              background: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              <CircularProgress />
                            </Box>
                          )}
                          <img
                            src={image}
                            alt={`${t('post.image')} ${index + 1}`}
                            onLoad={() => setImagesLoaded(prev => ({ ...prev, [index]: true }))}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            onClick={() => setSelectedImage(image)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                          >
                            <IonIcon icon={expand} style={{ color: 'white' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    // 5 or more images - show first 4 with a +X overlay on the last one
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gridTemplateRows: 'repeat(2, 150px)',
                      gap: 1,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)'
                    }}>
                      {post.images.slice(0, 4).map((image, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {!imagesLoaded[index] && (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center',
                              height: '100%',
                              width: '100%',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              background: 'rgba(255, 255, 255, 0.8)'
                            }}>
                              <CircularProgress />
                            </Box>
                          )}
                          <img
                            src={image}
                            alt={`${t('post.image')} ${index + 1}`}
                            onLoad={() => setImagesLoaded(prev => ({ ...prev, [index]: true }))}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {index === 3 && post.images.length > 4 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                cursor: 'pointer'
                              }}
                              onClick={() => setSelectedImage(post.images[3])}
                            >
                              +{post.images.length - 4}
                            </Box>
                          )}
                          <IconButton
                            onClick={() => setSelectedImage(image)}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0, 0, 0, 0.3)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': {
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                              display: index === 3 && post.images.length > 4 ? 'none' : 'flex'
                            }}
                          >
                            <IonIcon icon={expand} style={{ color: 'white' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                  
                </Box>
              )}

              {/* Likes and Date Section */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton 
                    onClick={handleLike} 
                    size="small" 
                    color="primary"
                    className={isLiked ? 'animate-pulse' : ''}
                    sx={{
                      transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                      '&:hover': {
                        transform: 'scale(1.2)'
                      }
                    }}
                  >
                    <IonIcon icon={isLiked ? heart : heartOutline} style={{ fontSize: '22px' }} />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t('post.likes_count', { count: likeCount })}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('post.posted_on', { date: formatDate(post.createdAt) })}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  lineHeight: 1.8,
                  color: 'text.primary',
                  fontSize: '1.05rem'
                }}
              >
                {post.description}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Improved Fullscreen Image Gallery Modal */}
        <Modal
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          closeAfterTransition
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(5px)'
          }}
        >
          <Fade in={!!selectedImage}>
            <Box sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.95)',
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}>
              {/* Header with close button */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100%',
                zIndex: 1300
              }}>
                <IconButton
                  onClick={() => setSelectedImage(null)}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              {/* Image counter */}
              {post.images.length > 1 && (
                <Box sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 1300,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: 4,
                  fontSize: '0.875rem',
                  fontWeight: 'medium'
                }}>
                  {post.images.findIndex(img => img === selectedImage) + 1} / {post.images.length}
                </Box>
              )}
              
              {/* Main Swiper Gallery */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}>
                <Swiper
                  modules={[Pagination, Navigation]}
                  pagination={{ 
                    clickable: true,
                    dynamicBullets: true,
                    dynamicMainBullets: 3
                  }}
                  navigation={{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                  }}
                  loop={post.images.length > 1}
                  initialSlide={Math.max(0, post.images.findIndex(img => img === selectedImage))}
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    '--swiper-navigation-color': '#fff',
                    '--swiper-pagination-color': '#fff',
                    '--swiper-navigation-size': '44px',
                    '--swiper-pagination-bullet-size': '10px',
                    '--swiper-pagination-bullet-inactive-color': '#999',
                    '--swiper-pagination-bullet-inactive-opacity': '0.5'
                  } as any}
                  spaceBetween={30}
                  speed={500}
                  grabCursor={true}
                  keyboard={{
                    enabled: true,
                  }}
                >
                  {post.images.map((image, index) => (
                    <SwiperSlide key={index}>
                      <Box sx={{
                        width: '100%',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: { xs: 2, sm: 4 }
                      }}>
                        <img 
                          src={image} 
                          alt={`${t('post.image')} ${index + 1}`}
                          style={{ 
                            maxWidth: '100%',
                            maxHeight: '85vh',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)'
                          }}
                        />
                      </Box>
                    </SwiperSlide>
                  ))}
                  
                  {/* Custom navigation buttons */}
                  {post.images.length > 1 && (
                    <>
                      <div className="swiper-button-prev" style={{
                        color: 'white',
                        background: 'rgba(0, 0, 0, 0.3)',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        left: '20px'
                      }}></div>
                      <div className="swiper-button-next" style={{
                        color: 'white',
                        background: 'rgba(0, 0, 0, 0.3)',
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        right: '20px'
                      }}></div>
                    </>
                  )}
                </Swiper>
              </Box>
            </Box>
          </Fade>
        </Modal>
      </IonContent>
    </IonPage>
  );
};
